import { useContext, useEffect, useState } from "react";
import {
  ClientData,
  ClientStatus,
  ConfigurationItem,
  DisplayResultMessage,
  ExchangeAccountType,
  GetWhitelistIpsRequest,
  LanguageNameSpaces,
  PrecisionConfig,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { useTheme } from "styled-components";
import {
  BINANCE_EXCHANGES,
  SPACE_SEPARATED_WHITELIST_IPS_EXCHANGES,
  EXCHANGES,
  SUPPORTED_WHITELIST_IP_EXCHANGES,
} from "@shared/common/constants";
import {
  getChildren,
  getIsMicaAccount,
  getItemKey,
  getName,
} from "@shared/features/configuration/utils";
import { CUSTOM_STRATEGIES_ITEM_NAME } from "@shared/features/configuration/constants";
import { PriceRange } from "@shared/features/tradingTerminal/common/types";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context";
import { getMinAndMaxPrices } from "@shared/features/tradingTerminal/common/utils";
import { INACTIVE_ACCOUNT_STATUSES } from "@shared/features/portfolio/constants";
import { useExchangesMetadata } from "@shared/utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

export const useAccountStatusTag = (
  status: ClientStatus,
  inDrawer?: boolean,
  hideInstructions?: boolean
) => {
  const theme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  let tagText = t("account_status.active");
  let tagColor = theme.colors.green;
  let popoverContent: string[] | undefined;
  if (status === ClientStatus.INVALID) {
    tagText = t("account_status.invalid");
    tagColor = theme.colors.red;
    popoverContent = inDrawer
      ? [
          t("account_status.invalid_drawer_message_1"),
          t("account_status.invalid_drawer_message_2"),
          t("account_status.invalid_drawer_message_3"),
        ]
      : [
          t("account_status.invalid_page_message_1"),
          t("account_status.invalid_page_message_2"),
        ];

    if (hideInstructions) {
      popoverContent = [popoverContent[0]];
    }
  } else if (INACTIVE_ACCOUNT_STATUSES.has(status)) {
    tagText = t("account_status.inactive");
    tagColor = theme.colors.darkYellow;
    if (status === ClientStatus.DELETED_BY_SUB_USER) {
      popoverContent = [t("account_status.sub_user_deleted")];
    } else if (status === ClientStatus.DELETED_BY_ASSET_MANAGER) {
      popoverContent = [t("account_status.asset_manager_deleted")];
    }
  }

  return {
    tagText,
    title: tagText + " Account",
    tagColor,
    popoverContent,
  };
};

export const useIPWhitelistTag = (inDrawer?: boolean, exchange?: string) => {
  const appTheme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);

  const isBinanceExchange = exchange && BINANCE_EXCHANGES.has(exchange);

  const articleId = isBinanceExchange
    ? inDrawer
      ? "6870965"
      : "6871341"
    : undefined;

  const baseText = inDrawer
    ? t("whitelist.drawer_message_2")
    : t("whitelist.page_message_2");

  const popoverTexts = [t("whitelist.message_1"), baseText];

  return {
    popoverTexts,
    articleId,
    tagColor: appTheme.colors.primaryBlue,
    tagText: t("whitelist.not_ip_whitelisted"),
  };
};

interface UseWhitelistIPsProps {
  args?: GetWhitelistIpsRequest;
  exchange?: string;
}

export const useWhitelistIPs = ({ args, exchange }: UseWhitelistIPsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { data: whitelistIPs } =
    sharedLib.sharedApi.configuration.useGetWhitelistIPsQuery(args);

  const joinChar =
    exchange && SPACE_SEPARATED_WHITELIST_IPS_EXCHANGES.has(exchange)
      ? " "
      : ",";

  return {
    important: t("whitelist.important"),
    mainMessage: t("whitelist.main_message"),
    copyText: whitelistIPs?.join(joinChar),
    notificationPrefix: "IP addresses were",
  };
};

export const useWhitelistIPsComponent = (exchange?: string) => {
  const [copyIPsClicked, setCopyIPsClicked] = useState(false);
  const [showCopyIPsWarning, setShowCopyIPsWarning] = useState(false);
  const isSupportedExchange =
    !!exchange && SUPPORTED_WHITELIST_IP_EXCHANGES.has(exchange);

  useEffect(() => {
    setShowCopyIPsWarning(false);
    setCopyIPsClicked(false);
  }, [exchange]);

  const checkAllowSave = () => {
    if (isSupportedExchange && !copyIPsClicked && !showCopyIPsWarning) {
      setShowCopyIPsWarning(true);
      return false;
    }
    return true;
  };

  return {
    checkAllowSave,
    copyIPsClicked,
    setCopyIPsClicked,
    showCopyIPsWarning,
    setShowCopyIPsWarning,
    isSupportedExchange,
  };
};

export const useChangeAccountStatus = (
  onSuccess: (() => void) | undefined,
  displayResultMessage: DisplayResultMessage
) => {
  const [changeClientsStatus, { isLoading }] =
    sharedLib.sharedApi.configuration.useChangeClientsStatusMutation();

  const handleChangeClientStatus = (
    clientIds?: number[],
    accountStatus?: ClientStatus,
    isRefreshKeys?: boolean
  ) => {
    if (!accountStatus || !clientIds || clientIds.length === 0) return;
    const isDelete = accountStatus === ClientStatus.DELETED;
    const isDeactivate = accountStatus === ClientStatus.DEACTIVATED_BY_USER;
    const isActivate = accountStatus === ClientStatus.ACTIVE;
    let actionName = "";
    if (isDelete) actionName = "delete";
    else if (isDeactivate) actionName = "deactivate";
    else if (isActivate) {
      actionName = isRefreshKeys ? "validate" : "activate";
    }
    const accountsText = clientIds.length > 1 ? "Accounts" : "Account";
    const successVerb = clientIds.length > 1 ? "were" : "was";
    const successMessage = `${accountsText} ${successVerb} ${actionName}d successfully.`;
    const errorMessage = `Failed to ${actionName} ${accountsText.toLowerCase()}.`;
    const promise = displayResultMessage(
      changeClientsStatus({
        client_ids: clientIds,
        status: accountStatus,
      }).unwrap(),
      successMessage,
      errorMessage
    );
    return promise.then(() => onSuccess?.()).catch(() => {});
  };

  return {
    isLoading,
    handleChangeClientStatus,
  };
};

export const useSharedCustomStrategies = (
  itemValue: any,
  itemChildren: ConfigurationItem[]
) => {
  const customStrategiesItem = itemChildren.find(
    (child) => getName(child) === CUSTOM_STRATEGIES_ITEM_NAME
  );
  const customStrategies = getChildren(customStrategiesItem);
  const selectedCustomStrategy = customStrategies.find(
    (strategy: ConfigurationItem) =>
      getItemKey(strategy, "value").type === itemValue
  );

  return {
    customStrategiesItem,
    customStrategies,
    selectedCustomStrategy,
  };
};

export const useSharedPriceRangeItem = (
  itemValue: PriceRange,
  originalItemValue: PriceRange,
  precisionConfig: PrecisionConfig,
  exchange?: string
) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const { getExchangePriceFactorLimits } = useContext(
    ExchangesMetadataContext
  )!;
  const priceFactorLimits = getExchangePriceFactorLimits(exchange);
  const priceLimits = getMinAndMaxPrices(
    originalItemValue.min,
    precisionConfig?.price || 0,
    priceFactorLimits
  );
  const validationMessage = t("messages.validation.price_min_max", {
    min: priceLimits.min,
    max: priceLimits.max,
  });
  const error =
    itemValue.min < priceLimits.min ||
    itemValue.min > priceLimits.max ||
    itemValue.max < priceLimits.min ||
    itemValue.max > priceLimits.max;

  return {
    validationMessage,
    error,
  };
};

export const useAccountAlternativeUsdPairs = (account?: ClientData) => {
  const { getExchangeMetadata } = useExchangesMetadata();
  const exchangeMetadata = getExchangeMetadata(account?.exchange);

  const getAccountData = (account?: ClientData) => {
    const isSinglePairWarning = exchangeMetadata?.pairs?.length === 1;
    const singlePair = isSinglePairWarning
      ? exchangeMetadata?.pairs?.[0]
      : undefined;
    const isMicaCreditMode =
      account?.exchange_account_type === ExchangeAccountType.MICA_CREDIT_MODE;
    const isBitmex = account?.exchange === EXCHANGES.BITMEX;
    const isInverse = exchangeMetadata?.is_inverse_exchange;
    const isDisabled =
      isSinglePairWarning || isMicaCreditMode || isBitmex || isInverse;
    const isDefaultEnabled =
      !isDisabled &&
      (getIsMicaAccount(account) ||
        account?.exchange === EXCHANGES.COINBASE_SPOT ||
        account?.exchange === EXCHANGES.COINBASE_FUTURES);

    return {
      isSinglePairWarning,
      isMicaCreditMode,
      isBitmex,
      isInverse,
      isDisabled,
      isDefaultEnabled,
      singlePair,
    };
  };

  return {
    ...getAccountData(account),
    getAccountData,
  };
};
